import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { BodyText } from "./Typography";
import { setActiveSeasonID } from "../lib/seasonPaths";
import useMobileView from "../central/useMobileview";

function TopBar() {
  const { currentUser } = useAuth();
  const [leagueData, setLeagueData] = useState(null);
  const [gameweekData, setGameweekData] = useState(null);
  const [teamName, setTeamName] = useState(null);
  const { isMobileView } = useMobileView();

  // Track the active GW listener so we can swap it out when GW advances
  const gwUnsubRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;

    let leagueUnsubscribe = null;

    (async () => {
      // 1. One-time fetch of user doc to get currentLeagueID and teamID
      const userSnap = await getDoc(doc(db, "users", currentUser.uid));
      if (!userSnap.exists()) return;

      const { currentLeagueID, teams = {} } = userSnap.data();
      if (!currentLeagueID) return;

      // 2. Live listener on the league doc — updates instantly when currentGW or name changes
      leagueUnsubscribe = onSnapshot(
        doc(db, "leagues", currentLeagueID),
        async (leagueSnap) => {
          if (!leagueSnap.exists()) return;
          const league = leagueSnap.data();
          const { currentSeasonID, currentGW } = league;
          if (!currentSeasonID || !currentGW) return;

          setActiveSeasonID(currentSeasonID);
          setLeagueData(league);

          // 3. Swap GW listener whenever currentGW changes
          if (gwUnsubRef.current) gwUnsubRef.current();
          gwUnsubRef.current = onSnapshot(
            doc(db, "leagues", currentLeagueID, "seasons", currentSeasonID, "gameweeks", currentGW),
            (gwSnap) => {
              if (gwSnap.exists()) setGameweekData(gwSnap.data());
            }
          );

          // 4. One-time fetch of team name (rarely changes)
          const teamID = teams[currentLeagueID];
          if (teamID) {
            const teamSnap = await getDoc(
              doc(db, "leagues", currentLeagueID, "seasons", currentSeasonID, "teams", teamID)
            );
            if (teamSnap.exists()) setTeamName(teamSnap.data().name);
          }
        }
      );
    })();

    return () => {
      if (leagueUnsubscribe) leagueUnsubscribe();
      if (gwUnsubRef.current) gwUnsubRef.current();
    };
  }, [currentUser]);

  if (!leagueData || !gameweekData) {
    return null;
  }

  const deadlineFormatted = gameweekData.deadline.toDate().toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  const containerStyle = {
    background: "linear-gradient(to bottom, #50B4C8, #9FD6E1)",
    color: "white",
    fontSize: "0.9rem",
    padding: "1px 16px",
    lineHeight: "1.2",
  };

  const leagueAndTeamName = teamName
    ? `${leagueData.name} • ${teamName}`
    : leagueData.name;

  if (isMobileView) {
    return (
      <div style={{ ...containerStyle, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <BodyText style={{ fontWeight: "bold", fontSize: "1.1rem", margin: "0 0 0px 0", color: "white" }}>{leagueAndTeamName}</BodyText>
        <BodyText style={{ margin: 0 ,fontSize: "0.8rem"}}>
          GW{gameweekData.number} &bull; Deadline: {deadlineFormatted}
        </BodyText>
      </div>
    );
  }

  return (
    <div
      style={{
        ...containerStyle,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        fontSize: "0.9rem",
      }}
    >
      <div style={{ position: "absolute", left: 16 }}>
        <BodyText style={{ color: "black", margin: 0 }}>
          Deadline: {deadlineFormatted}
        </BodyText>
      </div>
      <BodyText style={{ fontWeight: "bold", color: "white", margin: 0 }}>{leagueAndTeamName}</BodyText>
      <div style={{ position: "absolute", right: 16 }}>
        <BodyText style={{ color: "black", margin: 0 }}>GW{gameweekData.number}</BodyText>
      </div>
    </div>
  );
}

export default TopBar;
